import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SpaceAvatar } from "@/components/Avatars";
import { GeneralTab } from "@/components/settings/GeneralTab";
import { MembersTab } from "@/components/settings/MembersTab";
import { InvitationsTab } from "@/components/settings/InvitationsTab";
import { AuditTab } from "@/components/settings/AuditTab";
import { RealEstateSettingsTab } from "@/components/settings/RealEstateSettingsTab";
import { AutomationsTab } from "@/components/settings/AutomationsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function SpaceSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "general";

  const { data: ws, isLoading, error } = useQuery({
    queryKey: ["workspace", id],
    queryFn: async () => (await api.get(`/workspaces/${id}`)).data,
    retry: false,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", id],
    queryFn: async () => (await api.get(`/workspaces/${id}/members`)).data,
    enabled: !!ws,
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center space-y-3">
        <p className="font-heading font-semibold text-lg">{apiError(error)}</p>
        <Button variant="outline" onClick={() => navigate("/")}>Volver a Inicio</Button>
      </div>
    );
  }

  if (isLoading || !ws) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-4">
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!["owner", "admin"].includes(ws.my_role)) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center space-y-3">
        <p className="text-3xl">🔒</p>
        <p className="font-heading font-semibold text-lg">Solo los administradores pueden acceder a esta sección</p>
        <Button variant="outline" onClick={() => navigate(`/space/${id}`)}>Volver al Espacio</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6" data-testid="space-settings-page">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm" className="shrink-0" data-testid="settings-back-btn">
          <Link to={`/space/${id}`}><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <SpaceAvatar icon={ws.icon} color={ws.color} size="md" />
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight truncate">Administración</h1>
          <p className="text-xs text-muted-foreground truncate">{ws.name}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="w-full sm:w-auto flex overflow-x-auto justify-start">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="members" data-testid="settings-tab-members">Miembros</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="settings-tab-invitations">Invitaciones</TabsTrigger>
          <TabsTrigger value="automations" data-testid="settings-tab-automations">Automatizaciones</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit">Auditoría</TabsTrigger>
          {ws.modules?.realestate && <TabsTrigger value="realestate" data-testid="settings-tab-realestate">Inmobiliaria</TabsTrigger>}
        </TabsList>
        <TabsContent value="general" className="mt-6">
          <GeneralTab ws={ws} members={members} />
        </TabsContent>
        <TabsContent value="members" className="mt-6">
          <MembersTab ws={ws} members={members} />
        </TabsContent>
        <TabsContent value="invitations" className="mt-6">
          <InvitationsTab ws={ws} />
        </TabsContent>
        <TabsContent value="automations" className="mt-6">
          <AutomationsTab ws={ws} members={members} />
        </TabsContent>
        <TabsContent value="audit" className="mt-6">
          <AuditTab ws={ws} />
        </TabsContent>
        {ws.modules?.realestate && (
          <TabsContent value="realestate" className="mt-6">
            <RealEstateSettingsTab ws={ws} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
